import React, { useState, useMemo, useEffect } from "react";
import { Card, Button, Spin } from "antd";
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
  Cell,
} from "recharts";
import styles from "./styles.module.scss";
import Setting from "@assets/icons/setting";
import { WaterfallModal } from "./modal";
import { CfoApi, CfoWaterfallChartData } from "@services/cfo/cfo.api";
import { useDashboardStore } from "@pages/Dashboard/store/dashboard.state";

interface WaterfallChartProps {
  title?: string;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload?.length) {
    const { start, end } = payload[0].payload;
    const absVal = end - start;
    return (
      <div className={styles.tooltip}>
        <p className={styles.tooltipLabel}>{label}</p>
        <p className={styles.tooltipValue}>
          {absVal.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          })}
        </p>
      </div>
    );
  }
  return null;
};

const CHART_COLORS = {
  total_sales: "#5295E0", // Blue
  green: "#80C67A", // Green
  red: "#FE5858", // Red
  net_profit: "#5295E0", // Blue
};

const CHART_LABELS = {
  total_sales: "Total Sales",
  cost_of_goods_sold: "Cost of Goods Sold",
  overhead_cost: "Overhead Cost",
  opex: "OPEX",
  net_profit: "Net Profit",
};

const WaterfallChart: React.FC<WaterfallChartProps> = ({
  title = "Total Net Profit Value",
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<
    "Amazon" | "Shopify" | "TikTok"
  >("Amazon");

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiData, setApiData] = useState<CfoWaterfallChartData | null>(null);

  const { dateRange, asinSelected } = useDashboardStore();

  const cfoApi = useMemo(() => new CfoApi(), []);

  useEffect(() => {
    const fetchData = async () => {
      if (!dateRange?.startDate || !dateRange?.endDate) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const startDateString = dateRange.startDate.toISOString().split("T")[0];
        const endDateString = dateRange.endDate.toISOString().split("T")[0];
        const asin = asinSelected.length > 0 ? asinSelected[0] : "";

        console.log("Fetching waterfall chart data:", {
          startDate: startDateString,
          endDate: endDateString,
          asin,
          selectedChannel,
        });

        const response = await cfoApi.getWaterfallChartData({
          startDate: startDateString,
          endDate: endDateString,
          asin,
          salesChannel: selectedChannel,
        });

        console.log("Waterfall chart API response:", response);
        setApiData(response);
      } catch (err) {
        console.error("Error fetching waterfall chart data:", err);
        setError("Failed to load chart data. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [cfoApi, dateRange, asinSelected, selectedChannel]);

  const chartData = useMemo(() => {
    if (!apiData) return [];

    let cumulativeValue = apiData.total_sales;

    const steps = [
      {
        name: CHART_LABELS.total_sales,
        start: 0,
        end: apiData.total_sales,
        color: CHART_COLORS.total_sales,
        type: "positive" as const,
      },
      {
        name: CHART_LABELS.cost_of_goods_sold,
        start: cumulativeValue,
        end: cumulativeValue + apiData.cost_of_goods_sold,
        color:
          apiData.cost_of_goods_sold >= 0
            ? CHART_COLORS.green
            : CHART_COLORS.red,
        type: apiData.cost_of_goods_sold >= 0 ? "positive" : "negative",
      },
    ];

    cumulativeValue = cumulativeValue + apiData.cost_of_goods_sold;

    steps.push({
      name: CHART_LABELS.overhead_cost,
      start: cumulativeValue,
      end: cumulativeValue + apiData.overhead_cost,
      color: apiData.overhead_cost >= 0 ? CHART_COLORS.green : CHART_COLORS.red,
      type: apiData.overhead_cost >= 0 ? "positive" : "negative",
    });

    cumulativeValue = cumulativeValue + apiData.overhead_cost;

    steps.push({
      name: CHART_LABELS.opex,
      start: cumulativeValue,
      end: cumulativeValue + apiData.opex,
      color: apiData.opex >= 0 ? CHART_COLORS.green : CHART_COLORS.red,
      type: apiData.opex >= 0 ? "positive" : "negative",
    });

    steps.push({
      name: CHART_LABELS.net_profit,
      start: 0,
      end: apiData.net_profit,
      color: CHART_COLORS.net_profit,
      type: "positive" as const,
    });

    return steps.map((item) => {
      const delta = Math.abs(item.end - item.start);
      const base = Math.min(item.start, item.end);
      return {
        ...item,
        base,
        delta,
        sign: item.end >= item.start ? "positive" : "negative",
      };
    });
  }, [apiData]);

  return (
    <Card className={styles.waterfallCard}>
      <div className={styles.header}>
        <h2 className={styles.title}>{title}</h2>
        <Button
          className={styles.settingsButton}
          onClick={() => setIsModalOpen(true)}
          icon={<Setting />}
          type="text"
        />
      </div>

      <div className={styles.chartContainer}>
        {isLoading && (
          <div className={styles.loadingOverlay}>
            <Spin size="large" />
          </div>
        )}

        {error && <div className={styles.errorMessage}>{error}</div>}

        {!isLoading && !error && chartData.length > 0 && (
          <ResponsiveContainer width="100%" height={600}>
            <ComposedChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid stroke="#333" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fill: "#fff", fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                tickMargin={10}
              />
              <YAxis
                tick={{ fill: "#fff", fontSize: 12 }}
                axisLine={{ stroke: "#333", strokeWidth: 2 }}
                tickFormatter={(val) =>
                  `$${(val / 1000).toFixed(0)}${val !== 0 ? "K" : ""}`
                }
                tickMargin={20}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} />

              <Bar dataKey="base" stackId="water" fill="transparent" />

              <Bar dataKey="delta" stackId="water">
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${entry.name}-${index}`}
                    fill={entry.color}
                  />
                ))}
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      <WaterfallModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedChannel={selectedChannel}
        setSelectedChannel={setSelectedChannel}
      />
    </Card>
  );
};

export default WaterfallChart;
